import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isNotDefined } from '@sgme/fp';

import { isNotYetIntegrated } from '@/store/growl/notYetIntegrated.ts';
import type { DealExecutionId, DealExecutionStatus } from '@/store/growl/state/common.ts';
import type { Growl, GrowlId, GrowlOption, Growls } from '@/store/growl/state/index.ts';
import { removeKey } from '@/utils/stateMap.ts';

type DistributiveOmit<T, K extends PropertyKey> = T extends any ? Omit<T, K> : never;
export type GrowUpdate = DistributiveOmit<Growl['data'], 'tileId' | 'productName' | 'data'>;

export const GrowlSlice = createSlice({
  name: 'growls',
  initialState: {} as Growls,
  reducers: {
    create: (state, action: PayloadAction<{ growlId: GrowlId; growl: Growl }>) => {
      state[action.payload.growlId] = action.payload.growl;
    },

    update: (state, action: PayloadAction<{ growlId: GrowlId; data: GrowUpdate; options?: GrowlOption }>) => {
      const existingGrowlData = state[action.payload.growlId]?.data;
      if (isNotDefined(existingGrowlData)) {
        return;
      }

      state[action.payload.growlId] = {
        ...state[action.payload.growlId],
        data: { ...existingGrowlData, ...action.payload.data },
        options: { ...state[action.payload.growlId]?.options, ...action.payload.options },
      } as Growl;
    },

    delete: (state, action: PayloadAction<{ growlId: string }>) => {
      return removeKey(state, action.payload.growlId);
    },

    //
    // The following actions are not pure growl
    // ideally the logic in there should be in the caller
    // this is done (temporarily?) to help transition and should not be used for new features
    //
    fillReportReceived: (state, action: PayloadAction<{ growlId: DealExecutionId; tradeIds: string[] }>) => {
      const { growlId, tradeIds } = action.payload;

      const growl = state[growlId];

      if (isNotDefined(growl) || !('status' in growl.data) || !canReceiveFillReport(growl.data.status)) {
        return;
      }

      if (isNotYetIntegrated(tradeIds)) {
        if (growl.data.status === 'not-integrated') {
          return;
        }

        state[growlId] = {
          ...growl,
          data: {
            ...growl.data,
            status: 'not-integrated',
          },
          options: { closeable: true },
        } as Growl;

        return;
      }

      state[growlId] = {
        ...growl,
        data: {
          ...growl.data,
          status: 'confirmed',
          tradeIds,
        },
        options: { closeable: true },
      } as Growl;
    },
  },
  selectors: {
    getExecution: (state, executionId: DealExecutionId) => state[executionId],
  },
});

/**
 * A fill report is only meaningful while the deal is still waiting for its trade id.
 * `not-integrated` is included so that a late trade id can still be recovered.
 */
export function canReceiveFillReport(status: DealExecutionStatus): boolean {
  return status === 'still-pending' || status === 'integrated' || status === 'not-integrated';
}

export const { fillReportReceived } = GrowlSlice.actions;

export const { getExecution } = GrowlSlice.selectors;
